import { DateTime } from 'luxon';
import { match } from 'ts-pattern';
import { DateString, urlDateToUtcDateTime } from './UrlDate';

export const DaysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
export const DaysOfWeekShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const DaysOfWeekAvailability = [
  'availableSunday',
  'availableMonday',
  'availableTuesday',
  'availableWednesday',
  'availableThursday',
  'availableFriday',
  'availableSaturday',
] as const;

/**
 * @deprecated Do not use English strings for days of week. Some javascript APIs return localized day of week strings,
 * which has broken the site in the past for users whose browser is not set to English.
 */
export type DayOfWeek = (typeof DaysOfWeek)[number];
export type DayOfWeekAvailability = (typeof DaysOfWeekAvailability)[number];

export interface IDailyOptions {
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
}

export const allDays: Readonly<IDailyOptions> = {
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: true,
  sunday: true,
} as const;

export type IDayOfWeekAvailability = Record<DayOfWeekAvailability, boolean>;

export interface IDayOfWeekDelivery {
  mondayDelivery: boolean;
  tuesdayDelivery: boolean;
  wednesdayDelivery: boolean;
  thursdayDelivery: boolean;
  fridayDelivery: boolean;
  saturdayDelivery: boolean;
  sundayDelivery: boolean;
}

/**
 * Determine the delivery days of the week for an object containing IDayOfWeekDelivery fields.
 *
 * @param obj - An object that contains IDayOfWeekDelivery fields (and potentially other fields)
 * @returns An array of days of the week as numerical values (1 = Monday, 7 = Sunday)
 */
export function getDaysOfWeekFromDeliverySchedule<T extends Partial<IDayOfWeekDelivery>>(obj: T): number[] {
  const daysOfWeek = [
    { day: 1, field: 'mondayDelivery' },
    { day: 2, field: 'tuesdayDelivery' },
    { day: 3, field: 'wednesdayDelivery' },
    { day: 4, field: 'thursdayDelivery' },
    { day: 5, field: 'fridayDelivery' },
    { day: 6, field: 'saturdayDelivery' },
    { day: 7, field: 'sundayDelivery' },
  ];

  return daysOfWeek
    .filter(({ field }) => obj[field as keyof IDayOfWeekDelivery]) // Check if the field is true in the object
    .map(({ day }) => day); // Extract the day numbers
}
function throwInvalidDayNum(num: number): never {
  throw new Error(`Expected day of week number to be between 1 and 7, but got: ${num}`);
}

export function isDeliveryOnDayOfWeek(item: IDayOfWeekDelivery, dayOfWeek: DateTime | Date | DateString): boolean {
  return match(getDateTimeDayOfWeek(dayOfWeek))
    .with(1, () => item.mondayDelivery)
    .with(2, () => item.tuesdayDelivery)
    .with(3, () => item.wednesdayDelivery)
    .with(4, () => item.thursdayDelivery)
    .with(5, () => item.fridayDelivery)
    .with(6, () => item.saturdayDelivery)
    .with(7, () => item.sundayDelivery)
    .otherwise(throwInvalidDayNum);
}

// [ ] TODO: replace this most everywhere with new food item availability file's method
export function isAvailableOnDay(item: IDayOfWeekAvailability, date: DateTime): boolean {
  return match(getDateTimeDayOfWeek(date))
    .with(1, () => item.availableMonday)
    .with(2, () => item.availableTuesday)
    .with(3, () => item.availableWednesday)
    .with(4, () => item.availableThursday)
    .with(5, () => item.availableFriday)
    .with(6, () => item.availableSaturday)
    .with(7, () => item.availableSunday)
    .otherwise(throwInvalidDayNum);
}

// [ ] TODO: replace areas with new function
export function isAvailableOnAnyDay(item: IDayOfWeekAvailability): boolean {
  return (
    item.availableSunday ||
    item.availableMonday ||
    item.availableTuesday ||
    item.availableWednesday ||
    item.availableThursday ||
    item.availableFriday ||
    item.availableSaturday
  );
}

export function hasSomeDayAvailability(item: IDayOfWeekAvailability): boolean {
  return (
    item.availableSunday ||
    item.availableMonday ||
    item.availableTuesday ||
    item.availableWednesday ||
    item.availableThursday ||
    item.availableFriday ||
    item.availableSaturday
  );
}
export function getDateTimeDayOfWeek(date: Date | { weekday: number } | DateString): number {
  if (date instanceof Date) {
    // eslint-disable-next-line no-restricted-properties
    const weekday = date.getDay();
    return weekday === 0 ? 7 : weekday;
  }

  // Another workspace (shef-api) could call this function and provide it a DateTime,
  // but it wont be the same DateTime as the one included by common because shef-api
  // does NOT hoist dependencies.
  const isDateTimeLike = (date: unknown): date is { weekday: number } =>
    Boolean(
      // eslint-disable-next-line no-restricted-properties
      date && typeof (date as any).weekday === 'number'
    );

  if (isDateTimeLike(date)) {
    // eslint-disable-next-line no-restricted-properties
    return date.weekday;
  }

  // eslint-disable-next-line no-restricted-properties
  return urlDateToUtcDateTime(date).weekday;
}

export function isDateWithinTheNextWeek(date: Date): boolean {
  const dateToCheck = DateTime.fromJSDate(date);
  const now = DateTime.local();
  const nextWeek = now.plus({ days: 7 });
  return dateToCheck >= now && dateToCheck < nextWeek;
}

export const getUTCDayOfWeek = (date: Date): number => {
  const dayOfWeek = date.getUTCDay();
  return dayOfWeek === 0 ? 7 : dayOfWeek;
};
